/**
 * SEP 147: App Video
 */

import clsx from 'clsx';
import * as cfe from 'ego-cfe';
import * as api from 'ego-sdk-js';
import React from 'react';
import { useSelector } from 'react-redux';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

import Player from 'video.js/dist/types/player';

import * as Config from '../config';
import * as store from '../state/store';
import { isIosWebKit } from '../util';

import { ContentControlHandle } from './ContentControlContext';
import useAgentMode from './hooks/useAgentMode';
import useApiClient from './hooks/useApiClient';
import useToast from './hooks/useToast';
import ExpandIcon from './icon/ExpandIcon';
import ExploreIcon from './icon/ExploreIcon';
import MinusIcon from './icon/MinusIcon';
import PauseIcon from './icon/PauseIcon';
import PlaySolidIcon from './icon/PlaySolidIcon';
import PlusIcon from './icon/PlusIcon';
import RewindIcon from './icon/RewindIcon';
import VolumeIcon from './icon/VolumeIcon';
import VolumeXmarkIcon from './icon/VolumeXmark';
import Alert from './lib/Alert';
import Button from './lib/Button';
import { useModalManager } from './ModalManagerContext';
import { VideoProgressBar, VideoProgressBarMethods } from './ProgressBar';

const VideoInfoAgentModal = React.lazy(() => import('./VideoInfoAgentModal'));

interface TimelinePillMethods {
  updateDuration: (duration: number) => void;
  updateCurTime: (curTime: number) => void;
}

const TimelinePill = React.forwardRef<TimelinePillMethods, {}>((_, ref) => {
  const [duration, setDuration] = React.useState(0.0);
  const [curTime, setCurTime] = React.useState(0.0);
  React.useImperativeHandle(ref, () => ({
    updateCurTime: updatedCurTime => setCurTime(updatedCurTime),
    updateDuration: updatedDuration => setDuration(updatedDuration),
  }));
  return (
    <div
      className={clsx(
        'tw-absolute tw-invisible group-hover:tw-visible tw-bottom-0 tw-left-1/2 tw-transform -tw-translate-x-1/2',
        'tw-text-light tw-opacity-70',
        'tw-py-2 tw-px-2 tw-mb-[20px] tw-rounded-full tw-bg-slate-800 tw-font-semibold',
      )}
    >
      {cfe.Formatter.formatTime(curTime)} / {cfe.Formatter.formatTime(duration)}
    </div>
  );
});

export interface VideoSource {
  src: string;
  type: 'application/x-mpegURL' | 'application/dash+xml' | 'video/mp4' | 'video/quicktime';
}

interface VideoPlayerProps {
  sources: VideoSource[];
  recordStoryOpen?: () => void;
  startProgress?: cfe.ApiHelpers.MediaStartProgress;
  updateProgress?: (progress: number) => void;
  portrait?: boolean;
  noOverflow?: boolean;
  // Applies to portrait & non-portrait videos.
  maxHeightShort?: boolean;
  // If unset, grows to fit parent container.
  explicitPortraitVideoHeight?: boolean;
  forceNoAutoPlay?: boolean;
  focusPlay?: boolean;
  onReady?: () => void;
  // If `loopAfterOneEnd` is set, `onEnd()` is not called after its first
  // invocation.
  onEnd?: () => void;
  loopAfterOneEnd?: boolean; // Used for prexos
  gutterClassName?: string;
  containerClassName?: string;
  stopPropagation?: boolean;
  stimUrl?: string; // For Agent AI options & transcript fetching
  mainEntry?: api.feed.IFeedEntryPair;
  registerHandle?: (handle: ContentControlHandle) => void;
}

export const VideoPlayer = (props: VideoPlayerProps) => {
  const { setToast } = useToast();
  const autoPlayMediaGlobal =
    useSelector<store.IAppState, boolean>(state => state.autoPlayMedia) && !props.forceNoAutoPlay;

  const videoDivRef = React.useRef<HTMLDivElement | null>(null);
  const playerRef = React.useRef<Player | null>(null);
  // This state is really just to force a component re-render when the player is ready.
  const [ready, setReady] = React.useState(false);

  // playerState is always descriptive (not prescriptive) of the state reported
  // by VideoJS. Because it's updated via callbacks from VideoJS, it may be
  // out-of-date.
  const [playerState, setPlayerState] = React.useState<'unknown' | 'playing' | 'paused' | 'ended'>('unknown');
  const [audioMuted, setAudioMuted] = React.useState(isIosWebKit());
  const [playbackRate, setPlaybackRate] = React.useState(1.0);
  const prevTime = React.useRef(0);

  const agentMode = useAgentMode();
  const { pushModal } = useModalManager();
  const apiClient = useApiClient();

  const [transcripts, setTranscripts] = React.useState<api.stimulus.ITranscript[] | null>(null);
  const [showTranscript, setShowTranscript] = React.useState<api.stimulus.ITranscript | null>(null);

  const options = {
    aspectRatio: props.portrait ? '9:16' : '16:9',
    autoplay: autoPlayMediaGlobal,
    controls: false,
    fluid: true,
    preload: 'metadata',
    responsive: false,
    sources: props.sources,
  };

  // Register content control handler
  React.useEffect(() => {
    if (playerRef.current && props.registerHandle) {
      props.registerHandle({
        getProgress: () => {
          const curTime = playerRef.current?.currentTime();
          const duration = playerRef.current?.duration();
          if (curTime !== undefined && duration !== undefined) {
            return curTime / duration;
          } else {
            return 0;
          }
        },
        getProgressOffset: () => {
          return playerRef.current?.currentTime() ?? 0;
        },
        seek: (progress: number) => {
          if (playerRef.current) {
            playerRef.current.currentTime(progress);
            updateProgressBar(true);
          }
        },
      });
    }
  }, [props.registerHandle, playerRef.current]);

  //
  // Responsible for correctly initializing and disposing of the VideoJS player.
  //
  React.useEffect(() => {
    if (!videoDivRef.current) {
      throw Error('Unexpected');
    }
    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoElement = document.createElement('video-js');
      videoElement.setAttribute('playsinline', 'true');
      if (isIosWebKit()) {
        videoElement.setAttribute('muted', 'true');
      }

      videoDivRef.current.appendChild(videoElement);

      playerRef.current = videojs(videoElement, options, () => {
        if (playerRef.current) {
          setAudioMuted(playerRef.current.volume() === 0 || (playerRef.current.muted() ?? false));
        }
      });
      return () => {
        if (playerRef.current) {
          playerRef.current.dispose();
        }
      };
    }
  }, []);

  //
  // Resume at previous progress point
  //
  const startProgressUsed = React.useRef(false);
  React.useEffect(() => {
    if (!playerRef.current || !ready || props.startProgress === undefined || startProgressUsed.current) {
      return;
    }
    const reportedDuration = playerRef.current.duration();
    const startTs = cfe.ApiHelpers.mediaStartProgressToTimeOffset(props.startProgress, reportedDuration);
    if (startTs !== null) {
      startProgressUsed.current = true;
      playerRef.current.currentTime(startTs);
      updateProgressBar(true);
    }
  }, [ready]);

  //
  // START: History & Progress tracking
  //

  const recordedUrlOpen = React.useRef(false);
  React.useEffect(() => {
    if (playerState === 'playing' && !recordedUrlOpen.current) {
      if (props.recordStoryOpen) {
        props.recordStoryOpen();
      }
      recordedUrlOpen.current = true;
    }
  }, [playerState]);

  // Save progress when screen closed.
  const lastTimestamp = React.useRef<[number, number] | null>(null);
  React.useEffect(() => {
    return () => {
      if (!playerRef.current || !props.updateProgress || !lastTimestamp.current) {
        return;
      }
      props.updateProgress((100 * lastTimestamp.current[0]) / lastTimestamp.current[1]);
    };
  }, []);

  const ticksSinceProgressSave = React.useRef(0);

  //
  // END: History & Progress tracking
  //

  const updateProgressBar = (instantaneous: boolean) => {
    if (playerRef.current && progressBarRef.current) {
      const curTime = playerRef.current.currentTime();
      const reportedDuration = playerRef.current.duration();
      // WARN: Check undefined b/c curTime=0 should still pass predicate.
      if (curTime !== undefined && reportedDuration) {
        if (instantaneous) {
          progressBarRef.current.update((curTime / reportedDuration) * 100, 0);
        } else {
          const deltaTime = Math.min(reportedDuration - curTime, 1.0);
          progressBarRef.current.update(((curTime + deltaTime) / reportedDuration) * 100, deltaTime);
        }
      }
    }
  };

  //
  // Response for setting player callbacks
  //
  React.useEffect(() => {
    if (playerRef.current) {
      const handleTimeUpdate = () => {
        if (playerRef.current && progressBarRef.current && timelinePillRef.current) {
          const curTime = playerRef.current.currentTime();
          const reportedDuration = playerRef.current.duration();
          if (curTime && reportedDuration) {
            lastTimestamp.current = [curTime, reportedDuration];
            // Immediately update progress if the curTime went backwards. This
            // is especially important for when the video is set to loop. The
            // progress bar should instantly reset.
            updateProgressBar(playerRef.current.paused() || prevTime.current > curTime);
            prevTime.current = curTime;
            timelinePillRef.current.updateCurTime(curTime);
            timelinePillRef.current.updateDuration(reportedDuration);
          }
          // Ticks period ~0.25s (~4 ticks/sec), so save progress every 30s.
          ticksSinceProgressSave.current += 1;
          if (ticksSinceProgressSave.current > 120) {
            ticksSinceProgressSave.current = 0;
            if (props.updateProgress && curTime !== undefined && reportedDuration) {
              props.updateProgress((curTime / reportedDuration) * 100);
            }
          }
        }
      };
      playerRef.current.on('timeupdate', handleTimeUpdate);

      const handlePlay = () => {
        updateProgressBar(false);
        setPlayerState('playing');
      };
      playerRef.current.on('play', handlePlay);

      const handlePause = () => {
        updateProgressBar(true);
        setPlayerState('paused');
      };
      playerRef.current.on('pause', handlePause);

      const handleEnded = () => {
        // WARNING: This is only called if loop=false. Once loop is set, this
        // is never called. It's simply how the "ended" event works.
        if (!playerRef.current) {
          return;
        }
        if (!playerRef.current.loop() && props.onEnd) {
          setPlayerState('ended');
          props.onEnd();
        }
        if (props.loopAfterOneEnd) {
          playerRef.current.loop(true);
        }
      };
      playerRef.current.on('ended', handleEnded);
      const handleLoadedMetadata = () => {
        // Only at this point is the duration available.
        setReady(true);
        if (props.onReady) {
          props.onReady();
        }
      };
      playerRef.current.on('loadedmetadata', handleLoadedMetadata);

      return () => {
        if (playerRef.current && !playerRef.current.isDisposed()) {
          playerRef.current.off('timeupdate', handleTimeUpdate);
          playerRef.current.off('play', handlePlay);
          playerRef.current.off('pause', handlePause);
          playerRef.current.off('ended', handleEnded);
          playerRef.current.off('loadedmetadata', handleLoadedMetadata);
        }
      };
    }
  }, [ready, props.onEnd]);

  // Dispose the Video.js player when the functional component unmounts
  React.useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  //
  // Anytime play() is requested, we disallow all pausing to prevent an
  // internal play interruption error.
  // https://developer.chrome.com/blog/play-request-was-interrupted
  //
  // This requires us to track when play & pause are requested and at times
  // move the pause request to post-callback.
  //
  // This issue is primarily observed when videos are initially loaded and
  // focusPlay quickly sets their state to playing/paused.
  //
  const playRequested = React.useRef(false);
  const pauseRequested = React.useRef(false);
  const [userInteractionRequired, setUserInteractionRequired] = React.useState(false);
  const userInteractionRequiredReset = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const tryPlay = () => {
    if (!playerRef.current) {
      return;
    }
    if (
      playerRef.current.paused() &&
      (playerState === 'paused' || playerState === 'ended' || playerState === 'unknown')
    ) {
      playRequested.current = true;
      // If the user hasn't interacted with the DOM yet (they loaded the page
      // to this video player), the browser disallows auto playing so we keep
      // retrying until the user has made an interaction.
      const playRetryUntilInteraction = () => {
        if (!playerRef.current) {
          return;
        }
        // NOTE: Don't think this would ever not return a promise as the docs
        // say it would only happen if the browser didn't support promises.
        playerRef.current
          .play()
          ?.then(() => {
            playRequested.current = false;
            if (pauseRequested.current) {
              if (playerRef.current) {
                playerRef.current.pause();
              }
              pauseRequested.current = false;
            }
            updateProgressBar(true);
          })
          .catch(error => {
            if (
              error.code === 0 &&
              error.name === 'NotAllowedError' &&
              error.message.indexOf("user didn't interact with the document first") > 0 &&
              !pauseRequested.current
            ) {
              if (playRequested.current) {
                // It's possible for the play-request to have vanished because
                // focusPlay is no longer set. This happens when a user loads a
                // fresh page and swipes between slides before finally pressing
                // play/interacting with the page.
                setTimeout(playRetryUntilInteraction, 100);
              }
              // The user-interaction-required bit is maintained transiently
              // because it's possible that an interaction happens on a
              // different prexo slide and it won't be detectable by this video
              // without undesirably playing the video.
              setUserInteractionRequired(true);
              if (userInteractionRequiredReset.current) {
                clearTimeout(userInteractionRequiredReset.current);
              }
              userInteractionRequiredReset.current = setTimeout(() => setUserInteractionRequired(false), 500);
            }
          });
      };
      playRetryUntilInteraction();
    }
  };

  const tryPause = () => {
    if (!playerRef.current) {
      return;
    }
    if (playerState === 'playing') {
      if (playRequested.current) {
        pauseRequested.current = true;
      } else {
        playerRef.current.pause();
      }
    }
  };

  React.useEffect(() => {
    if (playerRef.current && ready && props.focusPlay !== undefined) {
      if (props.focusPlay) {
        tryPlay();
      } else {
        // For user-interaction-required case: If focusPlay is no longer set,
        // unset the play-request so that when user-interaction finally occurs
        // on a different slide, this video does not start playing.
        playRequested.current = false;
        tryPause();
      }
    }
  }, [ready, props.focusPlay]);

  const userTogglePlay = () => {
    if (!playerRef.current) {
      return;
    }
    if (userInteractionRequired) {
      return;
    }
    if (playerState === 'paused' || (playerState === 'unknown' && playerRef.current.paused())) {
      tryPlay();
    } else if (playerState === 'ended') {
      playerRef.current.currentTime(0);
      updateProgressBar(true);
      // play() in a setTimeout, otherwise, progress bar doesn't have enough
      // time to reset all the way to 0.
      setTimeout(() => playerRef.current?.play(), 50);
    } else if (playerState === 'playing') {
      tryPause();
    }
  };

  const timelinePillRef = React.useRef<TimelinePillMethods | null>(null);
  const progressBarRef = React.useRef<VideoProgressBarMethods | null>(null);

  // Shrink buttons for mobile-web. NOTE: This is not responsive.
  const smViewport = window.matchMedia(Config.mediaBreakpoints.smMax).matches;

  return (
    <div className={clsx('tw-flex tw-flex-col', props.containerClassName)}>
      <div
        onClick={props.stopPropagation ? e => e.stopPropagation() : undefined}
        className={clsx(
          'tw-relative tw-flex tw-justify-center tw-bg-black tw-outline tw-outline-black',
          props.portrait ? 'tw-h-full' : null,
        )}
        style={
          !props.portrait && !props.noOverflow
            ? {
                left: 'calc((min(max(70vw, 100%), 1024px) - 100%) / -2)',
                width: 'calc(min(max(70vw, 100%), 1024px))',
              }
            : undefined
        }
      >
        <div
          data-vjs-player
          className={clsx(
            'tw-relative',
            'tw-group',
            // Margin is for progress bar in portrait mode
            props.portrait ? 'tw-aspect-[9/16] tw-mb-[6px]' : 'tw-aspect-video tw-w-full',
            props.maxHeightShort ? 'tw-max-h-80' : null,
            props.explicitPortraitVideoHeight && props.portrait ? 'tw-youtube-container-portrait' : null,
          )}
          role="button"
          onClick={userTogglePlay}
          // NOTE: Unclear why onClick isn't sufficient and this is necessary.
          // Without it, tapping on a video on mobile does not toggle play.
          onTouchStart={userTogglePlay}
        >
          <div ref={videoDivRef} />
          <div className="tw-absolute tw-top-1/2 tw-left-1/2 tw-transform -tw-translate-x-1/2 -tw-translate-y-1/2">
            {props.sources.length === 0 ? <Alert variant="warn">Video being processed</Alert> : null}
          </div>
          <TimelinePill ref={timelinePillRef} />
          {playerState === 'paused' || playerState === 'unknown' ? (
            <div
              className={clsx(
                'tw-absolute tw-right-0 tw-bottom-0',
                'tw-text-light tw-opacity-70',
                'tw-p-6 tw-mr-4 tw-mb-5 tw-rounded-full tw-bg-slate-800',
              )}
            >
              <PauseIcon size="4rem" />
            </div>
          ) : null}
          <VideoProgressBar
            ref={progressBarRef}
            seek={progress => {
              if (playerRef.current) {
                const duration = playerRef.current.duration();
                if (duration) {
                  playerRef.current.currentTime(progress * duration);
                  updateProgressBar(true);
                  if (playerState === 'playing') {
                    // HACK: If the player is playing, seeking pauses the player.
                    // We manually restart the playing after a delay, otherwise,
                    // VideoJS complains that the currentTime() pause conflicts
                    // with the play.
                    setTimeout(() => {
                      if (playerRef.current) {
                        playerRef.current.play();
                      }
                    }, 30);
                  }
                }
              }
            }}
          />
        </div>
      </div>
      <div className={clsx('tw-my-2 sm:tw-my-4 tw-flex', props.gutterClassName)}>
        <div className="tw-w-full tw-flex tw-justify-between tw-flex-wrap tw-gap-1 sm:tw-gap-2">
          <div className="tw-flex tw-gap-x-2" onClick={props.stopPropagation ? e => e.stopPropagation() : undefined}>
            <Button sm={smViewport} className="tw-rounded-full" variant="secondary" onClick={userTogglePlay}>
              {playerState === 'paused' || playerState === 'unknown' ? (
                <PlaySolidIcon size="1rem" />
              ) : playerState === 'ended' ? (
                <RewindIcon size="1rem" />
              ) : (
                <PauseIcon size="1rem" />
              )}
            </Button>
            <Button
              sm={smViewport}
              className="tw-rounded-full"
              variant="secondary"
              onClick={() => {
                if (!playerRef.current) {
                  return;
                }
                playerRef.current.requestFullscreen();
              }}
            >
              <ExpandIcon size="1rem" />
            </Button>
            <div className="tw-flex">
              <Button
                sm={smViewport}
                className="tw-rounded-l-full"
                variant="secondary"
                onClick={() => {
                  if (!playerRef.current) {
                    return;
                  }
                  playerRef.current.playbackRate(playbackRate - 0.2);
                  setPlaybackRate(playbackRate - 0.2);
                }}
              >
                <MinusIcon size="1rem" />
              </Button>
              <Button
                sm={smViewport}
                className="tw-rounded-none tw-font-mono !tw-px-2"
                variant="secondary"
                onClick={() => {
                  if (!playerRef.current) {
                    return;
                  }
                  playerRef.current.playbackRate(1.0);
                  setPlaybackRate(1.0);
                }}
              >
                {playbackRate.toFixed(1)}x
              </Button>
              <Button
                sm={smViewport}
                className="tw-rounded-r-full"
                variant="secondary"
                onClick={() => {
                  if (!playerRef.current) {
                    return;
                  }
                  playerRef.current.playbackRate(playbackRate + 0.2);
                  setPlaybackRate(playbackRate + 0.2);
                }}
              >
                <PlusIcon size="1rem" />
              </Button>
            </div>
          </div>
          <div className="tw-flex tw-gap-x-2" onClick={props.stopPropagation ? e => e.stopPropagation() : undefined}>
            {agentMode && props.stimUrl ? (
              <Button
                sm={smViewport}
                className="tw-rounded-full"
                variant="secondary"
                onClick={() => {
                  pushModal({
                    component: modalProps => <VideoInfoAgentModal {...modalProps} stimUrl={props.stimUrl!} />,
                    dupKey: 'video-agent-info',
                    kind: 'generic',
                  });
                }}
              >
                ...
              </Button>
            ) : null}
            {props.mainEntry ? (
              <Button
                sm={smViewport}
                className="tw-rounded-full"
                variant="secondary"
                onClick={() => {
                  if (!props.mainEntry) {
                    return;
                  }
                  window.open(cfe.ApiHelpers.getUrlPathnameForEntry(props.mainEntry.entry.entry_id), '_blank');
                }}
              >
                <ExploreIcon size="1.1rem" />
              </Button>
            ) : null}
            <Button
              sm={smViewport}
              className="tw-rounded-full"
              variant={showTranscript ? 'primary' : 'secondary'}
              onClick={() => {
                if (!playerRef.current) {
                  return;
                }
                if (!props.stimUrl) {
                  return;
                }
                if (transcripts === null) {
                  apiClient.stimulusGetTranscripts({ url: props.stimUrl }).then(resp => {
                    if (!playerRef.current) {
                      return;
                    }
                    if (resp.kind === api.StatusCode.Ok) {
                      let found = false;
                      setTranscripts(resp.result.transcripts);
                      for (const transcript of resp.result.transcripts) {
                        const track = playerRef.current.addRemoteTextTrack(
                          {
                            default: false,
                            kind: 'captions',
                            src: transcript.url,
                            srclang: transcript.lang,
                          },
                          false,
                        );
                        (track as any).track.api_obj = transcript;
                        if (transcript.lang === 'en') {
                          (track as any).track.mode = 'showing';
                          setShowTranscript(transcript);
                          found = true;
                        }
                      }
                      if (!found) {
                        setToast({ header: 'No captions available', icon: 'frown' });
                      }
                    }
                  });
                } else {
                  if (showTranscript) {
                    const tracks = (playerRef.current as any).textTracks();
                    // tslint:disable-next-line
                    for (let i = 0; i < tracks.length; i++) {
                      const track = tracks[i];
                      track.mode = 'disabled';
                    }
                    setShowTranscript(null);
                  } else {
                    let found = false;
                    const tracks = (playerRef.current as any).textTracks();
                    // tslint:disable-next-line
                    for (let i = 0; i < tracks.length; i++) {
                      const track = tracks[i];
                      if (!track.src) {
                        // This is a special default track that isn't one that
                        // we've added.
                        continue;
                      }
                      const apiTranscript = track.api_obj;
                      if (apiTranscript.lang === 'en') {
                        track.mode = 'showing';
                        setShowTranscript(apiTranscript);
                        found = true;
                        break;
                      }
                    }
                    if (!found) {
                      setToast({ header: 'No captions available', icon: 'frown' });
                    }
                  }
                }
              }}
            >
              <span className={showTranscript ? 'tw-font-bold' : undefined}>CC</span>
            </Button>
            <Button
              sm={smViewport}
              className="tw-rounded-full"
              variant="secondary"
              onClick={() => {
                if (!playerRef.current) {
                  return;
                }
                if (audioMuted) {
                  playerRef.current.volume(1);
                  playerRef.current.muted(false);
                  setAudioMuted(false);
                } else {
                  playerRef.current.volume(0);
                  playerRef.current.muted(true);
                  setAudioMuted(true);
                }
              }}
            >
              {audioMuted ? <VolumeXmarkIcon size="1.1rem" /> : <VolumeIcon size="1.1rem" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
